import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Building2, Lock, Infinity, ArrowRight, Play, Pause, Loader2, Headphones, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, getLang, setLang } from "../api";
import { resolveImageUrl } from "../lib/imageUrl";
import LangChips from "../components/LangChips";

function fmt(s) {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default function Artwork() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [audioUri, setAudioUri] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioMode, setAudioMode] = useState("preview");
  const [currentLang, setCurrentLang] = useState(getLang());
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [position, setPosition] = useState(0);
  const audioRef = useRef(null);
  const lastModeRef = useRef("");

  const loadArtwork = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const lang = getLang();
      setCurrentLang(lang);
      const a = await api.getArtwork(id);
      setArtwork(a);
    } catch {
      toast.error("Impossibile caricare l'opera");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadArtwork(); }, [loadArtwork]);

  // Pre-load preview audio
  useEffect(() => {
    if (!artwork || artwork.unlocked) return;
    if (lastModeRef.current === `${artwork.id}:preview` && audioUri) return;
    api.getAudio(artwork.id, "preview")
      .then((r) => {
        setAudioUri(`data:${r.mime};base64,${r.audio_b64}`);
        setAudioMode("preview");
        lastModeRef.current = `${artwork.id}:preview`;
      })
      .catch(() => {});
  }, [artwork?.id, artwork?.unlocked]); // eslint-disable-line

  // Wire HTMLAudioElement events
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setPosition(el.currentTime);
    const onLoaded = () => setDuration(el.duration || 0);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnd = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnd);
    };
  }, [audioUri]);

  function loadAudio(kind) {
    if (!artwork) return;
    if (kind === "full" && !artwork.unlocked) {
      navigate(`/paywall/${artwork.id}?kind=artwork`);
      return;
    }
    const cacheKey = `${artwork.id}:${kind}`;
    const el = audioRef.current;
    if (lastModeRef.current === cacheKey && audioUri && el) {
      if (playing) el.pause();
      else el.play().catch(() => toast.error("Impossibile riprodurre l'audio"));
      return;
    }
    setAudioLoading(true);
    api.getAudio(artwork.id, kind)
      .then((r) => {
        setAudioUri(`data:${r.mime};base64,${r.audio_b64}`);
        setAudioMode(kind);
        lastModeRef.current = cacheKey;
        // Try autoplay on next tick
        setTimeout(() => {
          audioRef.current?.play().catch(() => {});
        }, 50);
      })
      .catch((e) => toast.error("Audio non disponibile", { description: e?.message }))
      .finally(() => setAudioLoading(false));
  }

  async function changeLang(lang) {
    if (lang === currentLang) return;
    setCurrentLang(lang);
    setLang(lang);
    try { audioRef.current?.pause(); } catch {}
    setAudioUri(null);
    lastModeRef.current = "";
    await loadArtwork();
  }

  if (loading || !artwork) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <Loader2 className="text-brand animate-spin" size={32} />
      </div>
    );
  }

  const progress = duration > 0 ? Math.min(1, position / duration) : 0;

  return (
    <div className="min-h-screen bg-[#111] page-enter">
      <audio ref={audioRef} src={audioUri || undefined} preload="auto" />
      {/* Hero */}
      <div className="relative h-[420px]">
        {(() => { const s = resolveImageUrl(artwork.image_url); return s ? (
          <img src={s} alt={artwork.title} onError={(e) => { e.target.style.display = "none"; }} className="absolute inset-0 w-full h-full object-cover" />
        ) : <div className="absolute inset-0 bg-brand" />; })()}
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative z-10 px-5 pt-5 flex items-center justify-between">
          <button
            data-testid="artwork-back-btn"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
            className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center backdrop-blur-sm"
          >
            <ChevronLeft className="text-white" size={20} />
          </button>
          <div data-testid="artwork-museum-pill" className="inline-flex items-center gap-1.5 bg-black/50 px-3 py-2 rounded-full backdrop-blur-sm">
            <Building2 className="text-white" size={14} />
            <span className="text-white text-xs font-medium">{artwork.museum_name}</span>
          </div>
        </div>
      </div>

      {/* Sheet */}
      <div className="-mt-16 relative z-20 bg-bg rounded-t-[28px] min-h-[60vh] px-5 pt-5 pb-16">
        <div className="w-11 h-1 bg-border rounded-full mx-auto mb-5" />
        <div className="text-[11px] tracking-[0.25em] font-semibold text-ink-soft">
          {artwork.artist.toUpperCase()} · {artwork.year}
        </div>
        <h1 data-testid="artwork-title" className="font-heading font-bold text-4xl sm:text-5xl text-ink leading-[1.05] tracking-tight mt-2 mb-6">
          {artwork.title}
        </h1>

        <div className="mb-6">
          <LangChips value={currentLang} onChange={changeLang} available={artwork.available_languages} />
        </div>

        {/* Player */}
        <div data-testid="audio-player" className="bg-white border border-border rounded-2xl p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-semibold tracking-widest text-white ${audioMode === "full" ? "bg-gold" : "bg-brand"}`}>
              {audioMode === "full" ? <Headphones size={12} /> : <Play size={12} />}
              {audioMode === "full" ? "AUDIO COMPLETO" : "ANTEPRIMA GRATUITA"}
            </div>
            <span className="text-xs font-medium text-ink-soft tabular-nums">{fmt(position)} / {fmt(duration)}</span>
          </div>
          <div className="h-1 bg-border rounded-full overflow-hidden">
            <div className="h-full bg-gold transition-all" style={{ width: `${progress * 100}%` }} />
          </div>
          <div className="flex items-center gap-3 mt-3">
            <button
              data-testid="audio-play-btn"
              onClick={() => loadAudio(audioMode)}
              disabled={audioLoading}
              className="w-12 h-12 rounded-full bg-brand text-white flex items-center justify-center hover:bg-brand-hover transition disabled:opacity-60"
            >
              {audioLoading ? <Loader2 className="animate-spin" size={22} /> : (playing ? <Pause size={22} /> : <Play size={22} />)}
            </button>
            <span className="text-sm text-ink-soft flex-1">
              {audioMode === "preview" ? "Ascolta l'introduzione gratuita" : "Hai accesso illimitato a quest'opera"}
            </span>
          </div>
        </div>

        <div className="text-[11px] tracking-[0.3em] font-semibold text-ink-soft mb-2">ANTEPRIMA</div>
        <p className="text-[15px] leading-relaxed text-ink mb-6">{artwork.preview_text}</p>

        {artwork.unlocked && artwork.full_text ? (
          <>
            <div className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
              <CheckCircle2 size={16} /> Storia completa sbloccata
            </div>
            <p className="text-[15px] leading-relaxed text-ink mb-6 whitespace-pre-line">{artwork.full_text}</p>
            <button
              data-testid="play-full-audio-btn"
              onClick={() => loadAudio("full")}
              className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white py-3.5 rounded-full font-semibold transition"
            >
              <Play size={18} /> Ascolta l'audio completo
            </button>
          </>
        ) : (
          <>
            <button
              data-testid="unlock-full-content-btn"
              onClick={() => navigate(`/paywall/${artwork.id}?kind=artwork`)}
              className="w-full flex items-center gap-3 bg-brand hover:bg-brand-hover text-white p-4 rounded-2xl shadow-lg shadow-brand/25 transition"
            >
              <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><Lock size={20} /></div>
              <div className="flex-1 text-left">
                <div className="font-semibold">Sblocca la storia completa</div>
                <div className="text-xs text-white/80 mt-0.5">Audio e testo integrale · €{artwork.price_eur.toFixed(2)}</div>
              </div>
              <ArrowRight size={20} />
            </button>

            <div className="flex items-center gap-3 my-4">
              <div className="flex-1 h-px bg-border" />
              <span className="text-[11px] tracking-[0.3em] font-semibold text-ink-soft">OPPURE</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <button
              data-testid="unlock-pass-travel-btn"
              onClick={() => navigate(`/paywall/${artwork.museum_id}?kind=museum_pass`)}
              className="w-full flex items-center gap-3 bg-gold hover:bg-gold-hover text-white p-4 rounded-2xl transition"
            >
              <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center"><Infinity size={20} /></div>
              <div className="flex-1 text-left">
                <div className="text-[10px] tracking-[0.3em] font-semibold text-white/85 mb-0.5">PASS TRAVEL</div>
                <div className="font-semibold">Sblocca tutto: {artwork.museum_name}</div>
                <div className="text-xs text-white/85 mt-0.5">€{artwork.museum_pass_price_eur.toFixed(2)} · accesso illimitato</div>
              </div>
              <ArrowRight size={20} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
